import {
  AiFillCheckCircle,
  AiOutlineClockCircle,
  AiOutlineUser,
} from "solid-icons/ai";
import {
  IoBriefcaseOutline,
  IoCashOutline,
  IoInformationCircleOutline,
  IoLockClosedOutline,
} from "solid-icons/io";
import { Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { OfferCategoryRow } from "@/domains/job-offer/components/offer-subcategory-chips";
import type { JobOfferV2WithCompanyId } from "@/domains/job-offer/types";
import { formatDateAgo } from "@/libs/date";
import { formatJobCompensation } from "@/libs/format";

type ProposalPreviewProps = {
  job: JobOfferV2WithCompanyId;
};

export function ProposalPreview(props: ProposalPreviewProps) {
  const { t } = useI18n();

  const isActive = () => props.job.is_active !== false;

  return (
    <Card class="bg-white text-left">
      <CardHeader class="space-y-1">
        <div class="flex items-start gap-2">
          <IoBriefcaseOutline class="w-5 h-5 text-gray-700 mt-0.5 shrink-0" />
          <div class="min-w-0">
            <CardTitle class="text-base">
              {t(
                "company.search.freelancers.send_proposal.preview.offer.title",
              )}
            </CardTitle>
            <CardDescription>
              {t(
                "company.search.freelancers.send_proposal.preview.offer.description",
              )}
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent class="space-y-5">
        <div class="space-y-1">
          <h2 class="text-lg font-semibold text-gray-900">{props.job.title}</h2>
          <OfferCategoryRow
            category={props.job.job_category}
            keys={props.job.job_subcategory}
            inactive={!isActive()}
            class="pt-1"
          />
        </div>

        <div class="h-px bg-gray-100" />

        <div class="space-y-4">
          <div class="flex items-start gap-3">
            <IoCashOutline class="w-5 h-5 text-gray-500 mt-0.5 shrink-0" />
            <div class="space-y-0.5">
              <p class="text-sm text-gray-600">
                {t(
                  "company.search.freelancers.send_proposal.preview.offer.fields.compensation",
                )}
              </p>
              <p class="text-base font-semibold text-gray-900">
                {formatJobCompensation(props.job)}
              </p>
            </div>
          </div>

          <div class="flex items-start gap-3">
            <AiOutlineClockCircle class="w-5 h-5 text-gray-500 mt-0.5 shrink-0" />
            <div class="space-y-0.5">
              <p class="text-sm text-gray-600">
                {t(
                  "company.search.freelancers.send_proposal.preview.offer.fields.duration",
                )}
              </p>
              <p class="text-base font-semibold text-gray-900">
                <Show
                  when={props.job.duration}
                  fallback={t("common.coming_soon")}
                >
                  {t(`joblist.jobOffer.duration.${props.job.duration!}`)}
                </Show>
              </p>
            </div>
          </div>

          <div class="flex items-start gap-3">
            <AiOutlineClockCircle class="w-5 h-5 text-gray-500 mt-0.5 shrink-0" />
            <div class="space-y-0.5">
              <p class="text-sm text-gray-600">
                {t(
                  "company.search.freelancers.send_proposal.preview.offer.fields.effort",
                )}
              </p>
              <p class="text-base font-semibold text-gray-900">
                <Show
                  when={props.job.effort}
                  fallback={t("common.coming_soon")}
                >
                  {t(`joblist.jobOffer.effort.${props.job.effort!}`) as string}
                </Show>
              </p>
            </div>
          </div>
        </div>

        <div class="h-px bg-gray-100" />

        <dl class="grid grid-cols-1 xl:grid-cols-2 gap-x-6 gap-y-3 text-left">
          <div class="flex flex-col gap-1">
            <dt class="flex items-center gap-2 text-sm text-gray-600">
              <AiOutlineUser class="w-4 h-4 shrink-0" />
              {t("profile.company.jobOffers.applicants")}
            </dt>
            <dd class="text-sm font-medium text-gray-900 pl-6">
              {props.job.applications_count ?? 0}
            </dd>
          </div>

          <div class="flex flex-col gap-1">
            <dt class="flex items-center gap-2 text-sm text-gray-600">
              <AiOutlineClockCircle class="w-4 h-4 shrink-0" />
              {t(
                "company.search.freelancers.send_proposal.preview.offer.fields.published",
              )}
            </dt>
            <dd class="text-sm font-medium text-gray-900 pl-6">
              {t("profile.company.jobOffers.daysAgo", {
                count: formatDateAgo(props.job.created_at) ?? "—",
              })}
            </dd>
          </div>

          <div class="flex flex-col gap-1">
            <dt class="flex items-center gap-2 text-sm text-gray-600">
              <AiFillCheckCircle
                classList={{
                  "text-green-600": isActive(),
                  "text-gray-400": !isActive(),
                }}
                class="w-4 h-4 shrink-0"
              />
              {t(
                "company.search.freelancers.send_proposal.preview.offer.fields.status",
              )}
            </dt>
            <dd class="pl-6">
              <Badge variant={isActive() ? "light-green" : "secondary"}>
                {isActive()
                  ? t(
                      "company.search.freelancers.send_proposal.preview.offer.values.status_active",
                    )
                  : t(
                      "company.search.freelancers.send_proposal.preview.offer.values.status_inactive",
                    )}
              </Badge>
            </dd>
          </div>

          <div class="flex flex-col gap-1">
            <dt class="flex items-center gap-2 text-sm text-gray-600">
              <IoLockClosedOutline class="w-4 h-4 shrink-0 text-amber-600" />
              {t(
                "company.search.freelancers.send_proposal.preview.offer.fields.visibility",
              )}
            </dt>
            <dd class="text-sm font-medium text-gray-900 pl-6">
              <Show
                when={props.job.visibility === "private"}
                fallback={t("profile.company.jobOffers.visibility.public")}
              >
                {t("profile.company.jobOffers.visibility.private")}
              </Show>
            </dd>
          </div>

          <div class="flex flex-col gap-1">
            <dt class="flex items-center gap-2 text-sm text-gray-600">
              <IoInformationCircleOutline class="w-4 h-4 shrink-0" />
              {t(
                "company.search.freelancers.send_proposal.preview.offer.fields.renewable",
              )}
            </dt>
            <dd class="text-sm font-medium text-gray-900 pl-6">
              <Show
                when={props.job.renewable === true}
                fallback={t("common.non_renewable")}
              >
                {t("common.renewable")}
              </Show>
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}
